import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Facility } from '../../../../core/models/model';
import { IncidentCategoryService } from '../../../../core/services/incident-category.service';
import { OrganizationUtilService } from '../../../../core/services/organization-util.service';
import _ from 'lodash';
import { ResponseModalComponent } from '../../response-modal/response-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DataService } from '../../../../core/services/data.service';
import { CameraService } from '../../../../core/services/camera.service';
import * as Cesium from 'cesium';
import { FacilityInfoDialogComponent } from '../../facility-info-dialog/facility-info-dialog.component';
import { DeviceService } from '../../../../core/services/device.service';
import { MultipleMessageComponent } from '../multiple-message/multiple-message.component';
import { OrganizationService } from '../../../../core/services/organization.service';

@Component({
  selector: 'app-campus-list',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './campus-list.component.html',
  styleUrl: './campus-list.component.css'
})

export class CampusListComponent {

  filteredCampus: Facility[] = [];
  query = '';
  @ViewChild('searchInput') searchInput!: ElementRef;

  searchQuery: string = '';
  loading: boolean = false;
  error: string | null = null;
  incidentCategoryList: Record<string, any[]> = {};

  @Input() facilityViewId?: string;
  dataItems: any[] = [];
  dataItemsFiltered: any[] = [];
  incidentPermissions: any = {};
  rawCampusRoles: any[] = [];
  campusRoles: any[] = [];
  filteredCampusRoles: any[] = [];
  campusCount: number = 0;

  constructor(
    private organizationUtilService: OrganizationUtilService, 
    private incidentCategoryService: IncidentCategoryService,
    private modalService: NgbModal,
    private dataService: DataService,
    private camera: CameraService,
    private deviceService: DeviceService,
    private organizationService: OrganizationService
  ) {}

  private sortCampus(campus: any[]): Facility[] {
    return [...campus].sort((a, b) => 
      (a.label?.toLowerCase() || '').localeCompare(b.label?.toLowerCase() || '')
    );
  }

  onSearch() {
    const query = this.query.trim().toLowerCase();
    if (!query) {
      this.filteredCampusRoles = this.campusRoles;
      this.campusCount = this.getCampusCount(this.campusRoles) || this.dataItems.length;
      this.dataItemsFiltered = this.sortCampus(this.dataItems);
    } else {
      this.filteredCampusRoles = this.filterTree(this.campusRoles, query);
      this.campusCount = this.getCampusCount(this.filteredCampusRoles);
      this.dataItemsFiltered = this.sortCampus(
        this.dataItems.filter(fac =>
          fac.label?.toLowerCase().includes(query)
        )
      );
    }
  }

  ngOnInit() {
    this.setData();
    this.getPanicButtons();
    this.getActionSessionForCreatePerimeter();
    this.getCampusRoles();
  }

  getCampusRoles() {
    this.organizationService.getCampusRoles().subscribe({
      next: (result) => {
        this.rawCampusRoles = result?.rows || [];
        this.buildCampusRolesTree();
      },
      error: () => {
        this.rawCampusRoles = [];
        this.campusRoles = [];
        this.filteredCampusRoles = [];
        this.campusCount = 0;
      },
    });
  }

  buildCampusRolesTree() {
    this.campusRoles = this.formatData(this.rawCampusRoles);
    this.campusCount = this.getCampusCount(this.campusRoles) || this.dataItems.length;
    this.onSearch();
  }

  resolveNodeItem(node: any): any {
    if (!node) return null;
    const perimeterId = String(node.perimeterId || node.id || '');

    // 1. Search in all organization entityLocations
    const myOrgs = this.organizationUtilService.myOrgs || {};
    for (const org of Object.values(myOrgs)) {
      const locations = typeof (org as any).getEntityLocations === 'function'
        ? (org as any).getEntityLocations()
        : (org as any).entityLocations || [];

      const found = locations.find((loc: any) => 
        String(loc.id) === perimeterId || 
        String(loc.buildingId) === String(node.id) ||
        String(loc.attributes?.basePerimeterId) === perimeterId
      );
      if (found) {
        return found;
      }
    }

    // 2. Check in myFacilityPins / myBluetoothFacilityPins / myPerimeters / papiPerimeters
    const facilityPin = this.organizationUtilService.data?.myFacilityPins?.[perimeterId] ||
      this.organizationUtilService.data?.myBluetoothFacilityPins?.[perimeterId];

    const papiPerimeter = this.organizationUtilService.data?.papiPerimeters?.[perimeterId];

    let coordinates = [0, 0];
    if (facilityPin?.position) {
      coordinates = [facilityPin.position.lon || 0, facilityPin.position.lat || 0];
    } else if (papiPerimeter?.geometry?.coordinates?.[0]?.[0]) {
      const coords = papiPerimeter.geometry.coordinates[0];
      const lng = coords.reduce((sum: number, c: any) => sum + Number(c[0]), 0) / coords.length;
      const lat = coords.reduce((sum: number, c: any) => sum + Number(c[1]), 0) / coords.length;
      coordinates = [lng, lat];
    }

    return {
      id: perimeterId,
      label: node.name,
      name: node.name,
      locationType: node.level?.toUpperCase() === 'CAMPUS' ? 'campus' : 'facility',
      organizationId: node.organizationId,
      buildingId: node.id,
      attributes: {
        bluetooth: true,
        facilityId: node.id,
        basePerimeterId: perimeterId,
        poc: papiPerimeter?.pocs || [],
        address: '',
        sops: []
      },
      geometry: {
        type: 'Point',
        coordinates: coordinates
      }
    };
  }

  formatData(nodes: any[]): any[] {
    if (!Array.isArray(nodes)) return [];
    return nodes.map(node => ({
      ...node,
      expanded: true,
      checked: false,
      item: this.resolveNodeItem(node),
      children: node.children && node.children.length > 0 ? this.formatData(node.children) : []
    }));
  }

  filterTree(nodes: any[], query: string): any[] {
    if (!query) return nodes;

    const result: any[] = [];
    for (const node of nodes) {
      const nameMatches = (node.name?.toLowerCase() || '').includes(query) ||
        (node.item?.label?.toLowerCase() || '').includes(query);

      const matchingChildren = node.children && node.children.length > 0 
        ? this.filterTree(node.children, query) 
        : [];

      if (nameMatches || matchingChildren.length > 0) {
        result.push({
          ...node,
          expanded: true,
          children: matchingChildren.length > 0 ? matchingChildren : (nameMatches ? node.children : [])
        });
      }
    }
    return result;
  }

  getCampusCount(nodes: any[]): number {
    if (!Array.isArray(nodes)) return 0;
    let count = 0;
    for (const node of nodes) {
      if (node.level?.toUpperCase() === 'CAMPUS') {
        count++;
      }
      if (node.children && node.children.length > 0) {
        count += this.getCampusCount(node.children);
      }
    }
    return count;
  }

  isLeafBuilding(node: any): boolean {
    if (!node) return false;
    return node.level?.toUpperCase() === 'BUILDING' || 
      (node.level?.toUpperCase() !== 'CAMPUS' && node.level?.toUpperCase() !== 'AREA' && (!node.children || node.children.length === 0));
  }

  toggle(node: any) {
    node.expanded = !node.expanded;
  }

  onCampusClick(node: any): void {
    const item = node.item || this.resolveNodeItem(node);
    this.goToCampus(item);
  }

  setData() {
    const orgIds = _.map(this.organizationUtilService.myOrgs, 'id'); 

    this.dataItems = [];
    this.dataItemsFiltered = [];

    orgIds.forEach(orgId => {
      const org = _.find(this.organizationUtilService.myOrgs, { id: orgId });
      if (!org) return;

      const entityLocations = typeof org.getEntityLocations === 'function'
        ? org.getEntityLocations()
        : org.entityLocations || [];

      for (const item of entityLocations) {
        if (item.locationType === 'campus') {
          if (this.organizationUtilService.data.myFacilityPins[item.id]) {
            if (this.facilityViewId) {
              if (item.id === this.facilityViewId) {
                this.dataItems.push(item);
                this.dataItemsFiltered.push(item);
                this.organizationUtilService.data.myFacilityPins[item.id].show();
              }
            } else {
              this.dataItems.push(item);
              this.dataItemsFiltered.push(item);
              this.organizationUtilService.data.myFacilityPins[item.id].show();
            }
          }
        }
      }
    });

    this.dataItems = _.orderBy(this.dataItems, ['organizationId'], ['asc']);
    this.dataItemsFiltered = _.orderBy(this.dataItemsFiltered, ['organizationId'], ['asc']);
    this.dataItems = _.uniqBy(this.dataItems, 'buildingId');
    this.dataItemsFiltered = _.uniqBy(this.dataItemsFiltered, 'buildingId');
    if (this.rawCampusRoles.length > 0) {
      this.buildCampusRolesTree();
    } else {
      this.campusCount = this.dataItemsFiltered.length;
      this.onSearch();
    }
  }


  orgIsGG(): boolean {
    return false;
  }
  containsBasePerimeter(item: any): boolean {
    if(item.attributes.basePerimeterId && item.attributes.basePerimeterId.length > 0){
      return true;
    }

    return false;
  }

  goToCampus(item: any): void {
    if (!item) return;
    const basePerimeterId = item.attributes?.basePerimeterId || item.id;
    const campusPerimeter = this.organizationUtilService.data.myPerimeters[basePerimeterId] ||
      this.organizationUtilService.data.myPerimeters[item.id] ||
      this.organizationUtilService.perimeters?.[basePerimeterId];

    if (campusPerimeter && typeof campusPerimeter.show === 'function') {
      campusPerimeter.show();
    }

    let entity = this.organizationUtilService.data.myBluetoothFacilityPins[item.id] ||
      this.organizationUtilService.data.myFacilityPins[item.id];

    const coords = item.geometry?.coordinates ||
      (entity?.position ? [entity.position.lon, entity.position.lat] : null);

    if (coords && coords[0] !== 0 && coords[1] !== 0) {
      this.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          coords[0], 
          coords[1], 
          5000
        ),
        complete: () => {
          entity = this.organizationUtilService.data.myBluetoothFacilityPins[item.id] ||
            this.organizationUtilService.data.myFacilityPins[item.id];
          if (entity && typeof entity.viewDetailsWindow === 'function') {
            entity.viewDetailsWindow(entity);
          }
          if (campusPerimeter && typeof campusPerimeter.show === 'function') {
            campusPerimeter.show();
          }
        }
      });
    } else if (entity && typeof entity.viewDetailsWindow === 'function') {
      entity.viewDetailsWindow(entity);
    }
  }

  goToBuilding(item: any): void {
    if (!item) return;
    const perimeterId = item.id || item.attributes?.basePerimeterId;
    let entity = this.organizationUtilService.data.myBluetoothFacilityPins[perimeterId] ||
      this.organizationUtilService.data.myFacilityPins[perimeterId];

    const coords = item.geometry?.coordinates ||
      (entity?.position ? [entity.position.lon, entity.position.lat] : null);

    if (coords && coords[0] !== 0 && coords[1] !== 0) {
      this.camera.flyTo({
        destination: Cesium.Cartesian3.fromDegrees(
          coords[0],
          coords[1],
          5000
        ),
        complete: () => {
          entity = this.organizationUtilService.data.myBluetoothFacilityPins[perimeterId] ||
            this.organizationUtilService.data.myFacilityPins[perimeterId];
          if (entity && typeof entity.viewDetailsWindow === 'function') {
            entity.viewDetailsWindow(entity);
          }
        }
      });
    } else if (entity && typeof entity.viewDetailsWindow === 'function') {
      entity.viewDetailsWindow(entity);
    }
  }

  getCampusInfo(event: Event, item: Facility) {
    const modalRef = this.modalService.open(FacilityInfoDialogComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.item = item;
  }

  messagePOC(event: Event, item: any): void {
    event.stopPropagation();

    const data: any = { items: [] };

    if (item.attributes.poc) {
      const userIds = _.map(item.attributes.poc, 'userId');

      this.deviceService.getUserProfiles(userIds)
        .then((results) => {
          results = _.uniqBy(results, 'arcAngelId');
          results = _.sortBy(results, 'name');
          _.forEach(results, (result) => {
            data.items.push({ userId: result.arcAngelId, name: result.name });
          });
          data.isNewFormat = true;

          if (data.items.length === 0) {
            this.showAlert('No users to notify');
            return;
          }
          this.openMultipleMessageModal('Send Message', data.items[0] , true)
        })
        .catch((error) => {
          console.error('Error getting user profile.', error);
        });
    } else {
      this.showAlert('The Facility POC is not defined.');
      return;
    }
  }
  public openMultipleMessageModal(
    title: string,
    user: any,
    showCancel: boolean = false
  ) {
    this.organizationService.getProfilesFromOrganization(user.userId).subscribe({
      next: (userInfo: any) => {
        const modalRef = this.modalService.open(MultipleMessageComponent, {
          size: 'md',
          centered: true,      
          backdrop: 'static',
          keyboard:false,
        });
        modalRef.componentInstance.title = title;
        modalRef.componentInstance.userInfo = userInfo;
        modalRef.componentInstance.showCancel = showCancel;
        modalRef.componentInstance.users = user; 
        return modalRef.result;
      },
      error: (err: any) => {
        console.error('Error fetching user profile:', err);
      }
    });
  }
  private showAlert(message: string) {
    const modalRef = this.modalService.open(ResponseModalComponent, {
      centered: true,
      windowClass: 'response-modal-top zindex',
      backdropClass: 'response-modal-backdrop-top',
    });
    modalRef.componentInstance.message = message;
    modalRef.componentInstance.showCancel = false;
    return modalRef.result;
  }
  getPanicButtons() {
    this.incidentCategoryService.getIncidentCategoryForBuilding().subscribe({
      next: (result: Record<string, any[]>) => {
        this.incidentCategoryList = result;
      },
      error: (error: any) => {
        console.error(error);
      }
    });
  }

   panicButtonPressed($event: any, item: any, cat: any, isCampusIncident: boolean): void {
    let label = '';
    let id;
    if (cat) {
      label = cat.label;
      id = cat.id;
    }
    this.organizationUtilService.setCampusState(isCampusIncident ? true : false);
    this.createIncident(label,item, id);
  }

  firePanicPressed(item: any): void {
    this.createIncident('Fire Emergency',item,  0);
  }
  medicalPanicPressed(item: any): void {
    this.createIncident('Medical Emergency',item,  1);
  }
  policePanicPressed(item: any): void {
    this.createIncident('Police Emergency',item,  2);
  }
  generalPanicPressed(item: any): void {
    this.createIncident('General Emergency',item,  3);
  }
  otherPanicPressed(item: any): void {
    this.createIncident('Other Emergency',item,  4);
  }
   private createIncident(
      label: string,item: any,
      organizationIncidentCategoryId: number
    ): void {
      const perimeterId = item.id;
     if (!this.checkIncidentPermission(perimeterId)) {
        const modalRef = this.modalService.open(ResponseModalComponent, {
          centered: true,
          backdrop: 'static',
          keyboard: false,
        });
        const component = modalRef.componentInstance as ResponseModalComponent;
        component.message =
          'You do not have permission to create incident for this building';
        component.showCancel = false;
        return;
      }
  
      const modalRef = this.modalService.open(ResponseModalComponent, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
      });
      const component = modalRef.componentInstance as ResponseModalComponent;
      component.message = `Are you sure you want to create an active ${label} incident?`;
      component.showCancel = true;
      modalRef.result
        .then((confirmed: boolean) => {
          if (confirmed) {
            this.incidentCategoryService.startFixedPerimeterCreation(
              item?.organizationId,
              perimeterId,
              label,
              undefined,
              true,
              organizationIncidentCategoryId
            );
          }
        })
        .catch(() => {});
    }
    private getActionSessionForCreatePerimeter(): void {
    this.dataService.getActionSessionForCreatePerimeter().subscribe({
      next: (result) => {
        this.incidentPermissions = result;
      },
      error: (error) => {
        console.error(error);
      },
    });
  }
  private checkIncidentPermission(perimeterId: number): boolean {
    const buildingOrganizationIds =
      this.getBuildingOrganizationIds(perimeterId);

    const privateRows = this.incidentPermissions.privatePerimeter?.rows || [];
    const publicRows = this.incidentPermissions.publicPerimeter?.rows || [];
    const permissions = [...privateRows, ...publicRows];

    let permissionOrgIds: any[] = [];
    permissions.forEach((p: any) => {
      if (Array.isArray(p.organizationIds)) {
        permissionOrgIds = permissionOrgIds.concat(p.organizationIds);
      }
    });

    permissionOrgIds = Array.from(new Set(permissionOrgIds));

    const hasIntersection = buildingOrganizationIds.some((id) =>
      permissionOrgIds.includes(id)
    );
    return hasIntersection;
  }
   private getBuildingOrganizationIds(perimeterId: number): any[] {
    return (this.organizationUtilService.myPerimetersForOrg || [])
      .filter((o: any) => o.perimeterId === perimeterId)
      .map((o: any) => o.organizationId);
  }


}